import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import ModuleHeader from "../components/ModuleHeader";
import { Table } from "antd";
import { eamApi } from "@/api/eamApi";
import { rem2px } from "@/utils/fontSize";
import ReactQRCode from "react-qr-code";
import { REM_CONFIG } from "@/utils/fontSize";
const WarningReminders = ({ className = "", initDate = "2025-09", }) => {
    const ROW_HEIGHT = rem2px(REM_CONFIG.rowHeight);
    const [isLoading, setIsLoading] = useState(true);
    const [tableHeight, setTableHeight] = useState(0);
    const [isScrolling, setIsScrolling] = useState(true);
    const [tableData, setTableData] = useState([]);
    // 二维码放大弹窗状态
    const [showQrPopup, setShowQrPopup] = useState(false);
    const [currentAssetCode, setCurrentAssetCode] = useState("");
    const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });
    const tableContainerRef = useRef(null);
    const scrollTimer = useRef(null);
    const scrollContainerRef = useRef(null);
    // 鼠标移入二维码，显示放大弹窗
    const handleQrMouseEnter = (e, code) => {
        setCurrentAssetCode(code);
        setPopupPos({ x: e.clientX + 15, y: e.clientY + 15 });
        setShowQrPopup(true);
        // 暂停表格自动滚动
        setIsScrolling(false);
    };
    // 鼠标移出，关闭弹窗，恢复自动滚动
    const handleQrMouseLeave = () => {
        setShowQrPopup(false);
        setIsScrolling(true);
    };
    const columns = [
        {
            title: "序号",
            key: "index",
            width: "15%",
            render: (_, __, index) => index + 1,
        },
        {
            title: "资产编号",
            dataIndex: "assetCode",
            key: "assetCode",
            width: "40%",
            align: "left",
            ellipsis: true,
        },
        {
            title: "资产名称",
            dataIndex: "assetName",
            key: "assetName",
            ellipsis: true,
            width: "40%",
            align: "left",
        },
        {
            title: "二维码",
            dataIndex: "assetCode",
            key: "assetCode",
            width: "20%",
            align: "center",
            render: (code) => (_jsx("div", { style: {
                    width: "100%",
                    height: `0.34rem`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                }, onMouseEnter: (e) => handleQrMouseEnter(e, code), onMouseLeave: handleQrMouseLeave, children: _jsx(ReactQRCode, { value: code, size: 20, fgColor: "#00ffff", bgColor: "transparent" }) })),
        },
    ];
    // 数据请求
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const res = await eamApi.shareAssets();
                const data = (res.data || []).map((item, index) => ({
                    ...item,
                    key: item.assetCode || `item-${index}`,
                    qty: item.qty || 0,
                }));
                setTableData(data);
            }
            catch (error) {
                console.error("获取数据失败:", error);
                setTableData([]);
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);
    // 高度计算
    useEffect(() => {
        if (!tableContainerRef.current)
            return;
        const calculateTableHeight = () => {
            const container = tableContainerRef.current;
            if (!container)
                return;
            const parent = container.parentElement;
            if (!parent)
                return;
            const header = container.previousElementSibling;
            const headerHeight = header?.offsetHeight || rem2px(0.3);
            const containerPadding = rem2px(REM_CONFIG.spacing.paddingX) * 2;
            const availableHeight = parent.clientHeight - headerHeight - containerPadding;
            const finalHeight = Math.max(availableHeight, 150);
            if (tableHeight !== finalHeight) {
                setTableHeight(finalHeight);
            }
            scrollContainerRef.current =
                container.querySelector(".ant-table-body");
        };
        const initTimer = setTimeout(calculateTableHeight, 100);
        const resizeObserver = new ResizeObserver(() => {
            calculateTableHeight();
        });
        if (tableContainerRef.current.parentElement) {
            resizeObserver.observe(tableContainerRef.current.parentElement);
        }
        return () => {
            clearTimeout(initTimer);
            resizeObserver.disconnect();
        };
    }, [tableData.length, tableHeight]);
    // 核心滚动逻辑
    useEffect(() => {
        if (scrollTimer.current) {
            clearInterval(scrollTimer.current);
            scrollTimer.current = null;
        }
        if (tableHeight <= 0 || !isScrolling || tableData.length <= 1) {
            return;
        }
        if (!scrollContainerRef.current)
            return;
        const scrollContainer = scrollContainerRef.current;
        scrollContainer.scrollTop =
            scrollContainer.scrollHeight - scrollContainer.clientHeight;
        scrollTimer.current = setInterval(() => {
            const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
            const scrollStep = REM_CONFIG.spacing.scrollStep;
            let newScrollTop = scrollTop + scrollStep;
            if (newScrollTop + clientHeight >= scrollHeight - 1) {
                newScrollTop = 0;
            }
            scrollContainer.scrollTop = newScrollTop;
        }, 50);
        return () => {
            if (scrollTimer.current) {
                clearInterval(scrollTimer.current);
            }
        };
    }, [tableHeight, isScrolling, tableData.length]);
    // 鼠标交互
    const handleMouseEnter = () => setIsScrolling(false);
    const handleMouseLeave = () => {
        setIsScrolling(true);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop =
                scrollContainerRef.current.scrollHeight -
                    scrollContainerRef.current.clientHeight;
        }
    };
    return (_jsxs("div", { className: `
        relative 
        bg-gradient-to-t 
        from-[rgba(7,31,75,0.5)] 
        to-[rgba(6,32,66,0.2)] 
        flex flex-col 
        h-full
        min-h-0
        ${className}
      `, children: [_jsx(ModuleHeader, { title: "\u5171\u4EAB\u8D44\u4EA7" }), _jsxs("div", { ref: tableContainerRef, className: "custom-table-container flex-1 w-full min-h-0", style: {
                    minHeight: 0,
                    width: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                }, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsx(Table, { columns: columns, dataSource: tableData, pagination: false, loading: isLoading, scroll: {
                            y: tableHeight > 0 ? tableHeight : undefined,
                            x: "100%",
                        }, style: {
                            width: "100%",
                            tableLayout: "fixed",
                        }, size: "small", onRow: (record, index) => ({
                            style: {
                                backgroundColor: (index ?? 0) % 2 === 0 ? "transparent" : "#062654",
                                height: `${ROW_HEIGHT}px`,
                                lineHeight: `${ROW_HEIGHT}px`,
                            },
                        }), locale: { emptyText: "" } }), showQrPopup && (_jsxs("div", { style: {
                            position: "fixed",
                            left: popupPos.x,
                            top: popupPos.y,
                            background: "rgba(6, 42, 88, 0.95)",
                            padding: "16px",
                            borderRadius: "6px",
                            boxShadow: "0 0 12px rgba(0,255,255,0.3)",
                            zIndex: 9999,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "12px",
                        }, children: [_jsx(ReactQRCode, { value: currentAssetCode, size: 180, fgColor: "#00ffff", bgColor: "transparent" }), _jsx("span", { style: { color: "#fff", fontSize: "16px" }, children: currentAssetCode })] })), _jsx("style", { children: `
            .custom-table-container .ant-table-wrapper,
            .custom-table-container .ant-spin-nested-loading,
            .custom-table-container .ant-spin-container,
            .custom-table-container .ant-table,
            .custom-table-container .ant-table-container,
            .custom-table-container .ant-table-content,
            .custom-table-container .ant-table-body {
              flex: 1;
              display: flex;
              flex-direction: column;
              min-height: 0;
            }

            .custom-table-container .ant-table,
            .custom-table-container .ant-table table {
              background-color: transparent !important;
              color: #FFFFFF !important;
              border: none !important;
              border-collapse: collapse !important;
              border-spacing: 0 !important;
            }

            .custom-table-container .ant-table-thead > tr > th {
              background-color: #062a58 !important;
              color: #00ffff !important;
              border: none !important;
              border-right: 1px solid #062a58 !important;
              font-weight: 600;
              height: ${ROW_HEIGHT}px !important;
              padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
              font-size: ${REM_CONFIG.fontSize.small} !important;
              white-space: nowrap;
              position: sticky;
              top: 0;
              z-index: 2;
            }

            .custom-table-container .ant-table-thead > tr > th:first-child {
              border-left: none !important;
            }
            .custom-table-container .ant-table-thead > tr > th:last-child {
              border-right: none !important;
            }

            .custom-table-container .ant-table-tbody > tr > td {
              color: #FFFFFF !important;
              border: none !important;
              border-bottom: 1px solid rgba(255,255,255,0.1) !important;
              height: ${ROW_HEIGHT}px !important;
              padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
              font-size: ${REM_CONFIG.fontSize.small} !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .custom-table-container .ant-table-tbody > tr:hover > td {
              background-color: #1A446E !important;
            }

            .custom-table-container .ant-table-body {
              scrollbar-width: none;
              -ms-overflow-style: none;
              overflow-y: auto !important;
              height: auto !important;
              min-height: 0 !important;
              padding-top: 0 !important;
              margin-top: 0 !important;
            }

            .custom-table-container .ant-table-body::-webkit-scrollbar {
              display: none;
            }

            .custom-table-container .ant-table-thead::after,
            .custom-table-container .ant-table-cell::before {
              display: none !important;
            }

            /* ✅ 核心修复1：隐藏Antd测量空行（解决顶部空白） */
            .custom-table-container .ant-table-measure-row {
              display: none !important;
              height: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
              border: none !important;
            }

            /* ✅ 核心修复2：清除tbody顶部间距，数据紧贴表头 */
            .custom-table-container .ant-table-tbody {
              margin-top: 0 !important;
              padding-top: 0 !important;
              border-top: none !important;
            }

            /* ✅ 核心修复3：无数据时完全隐藏占位，仅保留表头 */
            .custom-table-container .ant-table-placeholder {
              display: none !important;
              height: 0 !important;
              border: none !important;
            }
          ` })] })] }));
};
export default WarningReminders;
